import React, { useState, useEffect, useCallback } from 'react'
import './CalculatorModal.css'

const CalculatorModal = ({ isOpen, onClose }) => {
  const [activeCalculator, setActiveCalculator] = useState('Odds Converter')

  // Odds Converter / Comparison state - Side 1
  const [american1, setAmerican1] = useState('')
  const [decimal1, setDecimal1] = useState('')
  const [fractional1, setFractional1] = useState('')
  const [implied1, setImplied1] = useState('')

  // Odds Converter / Comparison state - Side 2
  const [american2, setAmerican2] = useState('')
  const [decimal2, setDecimal2] = useState('')
  const [fractional2, setFractional2] = useState('')
  const [implied2, setImplied2] = useState('')

  // General inputs
  const [stake, setStake] = useState('100')
  const [probability, setProbability] = useState('55')
  const [bankroll, setBankroll] = useState('1000')
  const [units, setUnits] = useState('1')
  const [result, setResult] = useState('')

  // Parlay legs
  const [parlayLegs, setParlayLegs] = useState(['-110', '+150', ''])

  // Round Robin
  const [rrLegs, setRrLegs] = useState(['-110', '+150', '+200', ''])
  const [rrSize, setRrSize] = useState('2')

  // Risk of Ruin
  const [winRate, setWinRate] = useState('55')
  const [avgWin, setAvgWin] = useState('100')
  const [avgLoss, setAvgLoss] = useState('100')
  const [riskTolerance, setRiskTolerance] = useState('5')

  const calculators = [
    "Odds Converter", "Odds Comparison", "Kelly Criterion", "Arbitrage", "+EV Calculator",
    "Parlay Calculator", "Hedge Calculator", "Vig Calculator",
    "Unit Size Calculator", "Middle Finder", "Round Robin Calculator",
    "Risk of Ruin Calculator"
  ]

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => { document.body.style.overflow = 'unset' }
  }, [isOpen])

  // ========== CONVERSION FUNCTIONS ==========

  const americanToDecimal = (american) => {
    const num = parseFloat(american)
    if (isNaN(num) || num === 0) return null
    if (num > 0) return (num / 100) + 1
    return (100 / Math.abs(num)) + 1
  }

  const decimalToAmerican = (decimal) => {
    const num = parseFloat(decimal)
    if (isNaN(num) || num <= 1) return null
    if (num >= 2) return `+${Math.round((num - 1) * 100)}`
    return `${Math.round(-100 / (num - 1))}`
  }

  const decimalToFractional = (decimal) => {
    const num = parseFloat(decimal)
    if (isNaN(num) || num <= 1) return null
    const profit = num - 1
    // Find closest common fraction
    const fractions = [
      [1,10],[1,9],[1,8],[1,7],[1,6],[1,5],[2,9],[1,4],[2,7],[3,10],[1,3],[7,20],[2,5],[9,20],[1,2],
      [11,20],[3,5],[5,8],[13,20],[7,10],[3,4],[4,5],[5,6],[9,10],[10,11],[1,1],[11,10],[6,5],[5,4],
      [11,8],[6,4],[13,8],[7,4],[15,8],[2,1],[9,4],[5,2],[11,4],[3,1],[7,2],[4,1],[9,2],[5,1],[6,1],
      [7,1],[8,1],[9,1],[10,1],[12,1],[14,1],[16,1],[20,1],[25,1],[33,1],[50,1],[100,1]
    ]
    let closest = [1, 1]
    let minDiff = Infinity
    for (const [n, d] of fractions) {
      const diff = Math.abs(n / d - profit)
      if (diff < minDiff) {
        minDiff = diff
        closest = [n, d]
      }
    }
    return `${closest[0]}/${closest[1]}`
  }

  const decimalToImplied = (decimal) => {
    const num = parseFloat(decimal)
    if (isNaN(num) || num <= 0) return null
    return (1 / num * 100).toFixed(2)
  }

  const impliedToDecimal = (implied) => {
    const num = parseFloat(implied)
    if (isNaN(num) || num <= 0 || num >= 100) return null
    return (100 / num).toFixed(4)
  }

  const fractionalToDecimal = (frac) => {
    if (!frac || !frac.includes('/')) return null
    const parts = frac.split('/')
    if (parts.length !== 2) return null
    const num = parseFloat(parts[0])
    const den = parseFloat(parts[1])
    if (isNaN(num) || isNaN(den) || den === 0) return null
    return (num / den) + 1
  }

  // ========== AUTO-FILL HANDLERS ==========

  const updateFromAmerican = useCallback((value, side) => {
    const dec = americanToDecimal(value)
    if (dec) {
      const frac = decimalToFractional(dec)
      const imp = decimalToImplied(dec)
      if (side === 1) {
        setDecimal1(dec.toFixed(4))
        setFractional1(frac || '')
        setImplied1(imp || '')
      } else {
        setDecimal2(dec.toFixed(4))
        setFractional2(frac || '')
        setImplied2(imp || '')
      }
    }
  }, [])

  const updateFromDecimal = useCallback((value, side) => {
    const dec = parseFloat(value)
    if (!isNaN(dec) && dec > 1) {
      const am = decimalToAmerican(dec)
      const frac = decimalToFractional(dec)
      const imp = decimalToImplied(dec)
      if (side === 1) {
        setAmerican1(am || '')
        setFractional1(frac || '')
        setImplied1(imp || '')
      } else {
        setAmerican2(am || '')
        setFractional2(frac || '')
        setImplied2(imp || '')
      }
    }
  }, [])

  const updateFromFractional = useCallback((value, side) => {
    const dec = fractionalToDecimal(value)
    if (dec) {
      const am = decimalToAmerican(dec)
      const imp = decimalToImplied(dec)
      if (side === 1) {
        setAmerican1(am || '')
        setDecimal1(dec.toFixed(4))
        setImplied1(imp || '')
      } else {
        setAmerican2(am || '')
        setDecimal2(dec.toFixed(4))
        setImplied2(imp || '')
      }
    }
  }, [])

  const updateFromImplied = useCallback((value, side) => {
    const dec = impliedToDecimal(value)
    if (dec) {
      const decNum = parseFloat(dec)
      const am = decimalToAmerican(decNum)
      const frac = decimalToFractional(decNum)
      if (side === 1) {
        setAmerican1(am || '')
        setDecimal1(dec)
        setFractional1(frac || '')
      } else {
        setAmerican2(am || '')
        setDecimal2(dec)
        setFractional2(frac || '')
      }
    }
  }, [])

  // ========== CALCULATOR FUNCTIONS ==========

  const calculateKellyCriterion = () => {
    const p = parseFloat(probability) / 100
    const oddsNum = parseFloat(american1 || '-110')
    const b = oddsNum > 0 ? oddsNum / 100 : 100 / Math.abs(oddsNum)
    const kelly = ((b * p) - (1 - p)) / b
    const bankrollAmount = parseFloat(bankroll)
    if (kelly > 0) {
      const betAmount = (kelly * bankrollAmount).toFixed(2)
      const halfKelly = (kelly * 0.5 * bankrollAmount).toFixed(2)
      const quarterKelly = (kelly * 0.25 * bankrollAmount).toFixed(2)
      setResult(`Full Kelly: $${betAmount} (${(kelly * 100).toFixed(2)}% of bankroll)\nHalf Kelly: $${halfKelly} (${(kelly * 50).toFixed(2)}%)\nQuarter Kelly: $${quarterKelly} (${(kelly * 25).toFixed(2)}%)\n\nKelly Fraction: ${kelly.toFixed(4)}\nEdge: ${((p * (b + 1) - 1) * 100).toFixed(2)}%`)
    } else {
      setResult('No bet recommended - negative expected value\n\nKelly Fraction: ' + kelly.toFixed(4) + '\nThis means the odds don\'t justify a bet at this win probability.')
    }
  }

  const calculateArbitrage = () => {
    const odds1 = parseFloat(american1 || '-110')
    const odds2Val = parseFloat(american2 || '+110')
    const stakeAmount = parseFloat(stake)
    const dec1 = americanToDecimal(odds1)
    const dec2 = americanToDecimal(odds2Val)
    if (!dec1 || !dec2) { setResult('Please enter valid odds'); return }

    const impliedProb1 = 1 / dec1
    const impliedProb2 = 1 / dec2
    const totalProb = impliedProb1 + impliedProb2

    if (totalProb < 1) {
      const arbPercent = ((1 - totalProb) * 100).toFixed(2)
      const bet1 = (stakeAmount * impliedProb1 / totalProb).toFixed(2)
      const bet2 = (stakeAmount * impliedProb2 / totalProb).toFixed(2)
      const profit = (stakeAmount / totalProb - stakeAmount).toFixed(2)
      const roi = ((profit / stakeAmount) * 100).toFixed(2)
      setResult(`✅ ARBITRAGE FOUND!\n\nArb %: ${arbPercent}%\n\nBet 1: $${bet1} @ ${odds1 > 0 ? '+' : ''}${odds1}\nBet 2: $${bet2} @ ${odds2Val > 0 ? '+' : ''}${odds2Val}\nTotal Risked: $${stakeAmount}\n\nGuaranteed Profit: $${profit}\nROI: ${roi}%`)
    } else {
      const overround = ((totalProb - 1) * 100).toFixed(2)
      setResult(`❌ No Arbitrage Opportunity\n\nTotal Implied: ${(totalProb * 100).toFixed(2)}%\nOverround/Vig: ${overround}%\n\nNeed combined implied < 100% for arb`)
    }
  }

  const calculateEV = () => {
    const oddsNum = parseFloat(american1 || '-110')
    const prob = parseFloat(probability) / 100
    const stakeAmount = parseFloat(stake)
    const dec = americanToDecimal(oddsNum)
    if (!dec) { setResult('Please enter valid odds'); return }

    const ev = (prob * (dec - 1) - (1 - prob)) * stakeAmount
    const evPercent = (ev / stakeAmount * 100).toFixed(2)
    const impliedProb = (1 / dec * 100).toFixed(2)
    const edge = (prob * 100 - parseFloat(impliedProb)).toFixed(2)

    if (ev > 0) {
      setResult(`✅ +EV OPPORTUNITY!\n\nExpected Value: +$${ev.toFixed(2)}\nEV%: +${evPercent}%\n\nYour Edge: ${edge}%\nTrue Probability: ${(prob * 100).toFixed(1)}%\nImplied Probability: ${impliedProb}%\n\nRecommended: Bet if edge > 2-3%`)
    } else {
      setResult(`❌ Negative EV\n\nExpected Value: -$${Math.abs(ev).toFixed(2)}\nEV%: ${evPercent}%\n\nYour Edge: ${edge}%\nTrue Probability: ${(prob * 100).toFixed(1)}%\nImplied Probability: ${impliedProb}%\n\nRecommended: Pass on this bet`)
    }
  }

  const calculateParlay = () => {
    const legs = parlayLegs.filter(l => l.trim() !== '')
    if (legs.length < 2) { setResult('Enter at least 2 legs'); return }

    let combinedDecimal = 1
    let legDetails = []
    for (let i = 0; i < legs.length; i++) {
      const dec = americanToDecimal(parseFloat(legs[i]))
      if (!dec) { setResult(`Invalid odds on leg ${i + 1}`); return }
      combinedDecimal *= dec
      legDetails.push(`Leg ${i + 1}: ${parseFloat(legs[i]) > 0 ? '+' : ''}${legs[i]} (${dec.toFixed(3)} decimal)`)
    }

    const stakeAmount = parseFloat(stake)
    const payout = (stakeAmount * combinedDecimal).toFixed(2)
    const profit = (payout - stakeAmount).toFixed(2)
    const impliedProb = (1 / combinedDecimal * 100).toFixed(2)

    setResult(`${legDetails.join('\n')}\n\n` +
      `Combined Odds: ${decimalToAmerican(combinedDecimal)} (${combinedDecimal.toFixed(3)} decimal)\n` +
      `Implied Probability: ${impliedProb}%\n\n` +
      `Stake: $${stakeAmount}\n` +
      `Payout: $${payout}\n` +
      `Profit: $${profit}`)
  }

  const calculateHedge = () => {
    const initialStake = parseFloat(stake)
    const initialOdds = parseFloat(american1 || '-110')
    const hedgeOdds = parseFloat(american2 || '+110')
    const targetProfit = parseFloat(bankroll)

    const dec1 = americanToDecimal(initialOdds)
    const dec2 = americanToDecimal(hedgeOdds)
    if (!dec1 || !dec2) { setResult('Please enter valid odds'); return }

    const initialPayout = initialStake * dec1

    // Calculate hedge bet for equal profit both ways
    const hedgeBet = (initialPayout - targetProfit) / dec2
    const hedgePayout = hedgeBet * dec2

    const profitIfOriginalWins = initialPayout - initialStake - hedgeBet
    const profitIfHedgeWins = hedgePayout - hedgeBet - initialStake

    if (hedgeBet < 0) {
      setResult(`No hedge needed - you're already guaranteed profit!\n\nInitial bet wins: $${(initialPayout - initialStake).toFixed(2)} profit`)
    } else {
      setResult(`Initial Bet: $${initialStake} @ ${initialOdds > 0 ? '+' : ''}${initialOdds}\n` +
        `Potential Payout: $${initialPayout.toFixed(2)}\n\n` +
        `Hedge Bet: $${hedgeBet.toFixed(2)} @ ${hedgeOdds > 0 ? '+' : ''}${hedgeOdds}\n\n` +
        `If original wins: $${profitIfOriginalWins.toFixed(2)} profit\n` +
        `If hedge wins: $${profitIfHedgeWins.toFixed(2)} profit\n\n` +
        `Total Risked: $${(initialStake + hedgeBet).toFixed(2)}`)
    }
  }

  const calculateVig = () => {
    const odds1 = parseFloat(american1 || '-110')
    const odds2 = parseFloat(american2 || '-110')
    const dec1 = americanToDecimal(odds1)
    const dec2 = americanToDecimal(odds2)
    if (!dec1 || !dec2) { setResult('Please enter valid odds'); return }

    const impliedProb1 = 1 / dec1
    const impliedProb2 = 1 / dec2
    const totalProb = impliedProb1 + impliedProb2
    const vig = (totalProb - 1) * 100

    const fairProb1 = impliedProb1 / totalProb
    const fairProb2 = impliedProb2 / totalProb
    const fairDec1 = 1 / fairProb1
    const fairDec2 = 1 / fairProb2

    setResult(`Side 1: ${odds1 > 0 ? '+' : ''}${odds1}\n` +
      `  Implied: ${(impliedProb1 * 100).toFixed(2)}%\n` +
      `  Fair: ${(fairProb1 * 100).toFixed(2)}% (${decimalToAmerican(fairDec1)})\n\n` +
      `Side 2: ${odds2 > 0 ? '+' : ''}${odds2}\n` +
      `  Implied: ${(impliedProb2 * 100).toFixed(2)}%\n` +
      `  Fair: ${(fairProb2 * 100).toFixed(2)}% (${decimalToAmerican(fairDec2)})\n\n` +
      `Total Implied: ${(totalProb * 100).toFixed(2)}%\n` +
      `Vig/Juice: ${vig.toFixed(2)}%`)
  }

  const calculateUnitSize = () => {
    const bankrollAmt = parseFloat(bankroll)
    const unitPercent = parseFloat(units)
    const unitSize = (bankrollAmt * unitPercent / 100).toFixed(2)

    setResult(`Bankroll: $${bankrollAmt}\nUnit Size: ${unitPercent}%\n\n` +
      `1 unit = $${unitSize}\n` +
      `2 units = $${(unitSize * 2).toFixed(2)}\n` +
      `3 units = $${(unitSize * 3).toFixed(2)}\n` +
      `5 units = $${(unitSize * 5).toFixed(2)}\n` +
      `10 units = $${(unitSize * 10).toFixed(2)}\n\n` +
      `Max risk (3%): $${(bankrollAmt * 0.03).toFixed(2)}\n` +
      `Aggressive (5%): $${(bankrollAmt * 0.05).toFixed(2)}`)
  }

  const calculateMiddleFinder = () => {
    const spread1 = parseFloat(american1 || '-3.5')
    const spread2 = parseFloat(american2 || '+4.5')

    const middle = spread2 - Math.abs(spread1)

    if (middle > 0) {
      setResult(`✅ MIDDLE OPPORTUNITY!\n\n` +
        `Line 1: ${spread1 > 0 ? '+' : ''}${spread1}\n` +
        `Line 2: ${spread2 > 0 ? '+' : ''}${spread2}\n\n` +
        `Middle Window: ${middle} points\n\n` +
        `If final margin lands between ${Math.abs(spread1)} and ${spread2}:\n` +
        `→ BOTH BETS WIN!\n\n` +
        `Example: Team wins by 4\n` +
        `  Bet 1 (${spread1}): Covers ✅\n` +
        `  Bet 2 (+${spread2}): Covers ✅`)
    } else if (middle === 0) {
      setResult(`⚠️ No middle - lines are the same\n\n` +
        `Line 1: ${spread1}\nLine 2: ${spread2}\n\n` +
        `Gap: 0 points (push possible on both)`)
    } else {
      setResult(`❌ No middle opportunity\n\n` +
        `Line 1: ${spread1}\nLine 2: ${spread2}\n\n` +
        `Gap: ${middle} points (negative = overlap, not a middle)`)
    }
  }

  const calculateRoundRobin = () => {
    const legs = rrLegs.filter(l => l.trim() !== '')
    const size = parseInt(rrSize)

    if (legs.length < size) {
      setResult(`Need at least ${size} legs for ${size}-leg round robin`)
      return
    }

    // Calculate all combinations of given size
    const combinations = []
    const combine = (start, combo) => {
      if (combo.length === size) {
        combinations.push([...combo])
        return
      }
      for (let i = start; i < legs.length; i++) {
        combo.push(legs[i])
        combine(i + 1, combo)
        combo.pop()
      }
    }
    combine(0, [])

    const stakeAmount = parseFloat(stake)
    const stakePerParlay = stakeAmount / combinations.length

    let totalPayout = 0
    let details = []

    for (const combo of combinations) {
      let comboDecimal = 1
      for (const leg of combo) {
        const dec = americanToDecimal(parseFloat(leg))
        if (!dec) { setResult('Invalid odds in one of the legs'); return }
        comboDecimal *= dec
      }
      const payout = stakePerParlay * comboDecimal
      totalPayout += payout
      details.push(`(${combo.join(', ')}): ${decimalToAmerican(comboDecimal)} → $${payout.toFixed(2)}`)
    }

    setResult(`Round Robin: ${legs.length} picks, ${size}-leg parlays\n` +
      `Total Combinations: ${combinations.length}\n` +
      `Stake per parlay: $${stakePerParlay.toFixed(2)}\n` +
      `Total Stake: $${stakeAmount}\n\n` +
      `Parlays:\n${details.join('\n')}\n\n` +
      `If ALL win:\n` +
      `Total Payout: $${totalPayout.toFixed(2)}\n` +
      `Profit: $${(totalPayout - stakeAmount).toFixed(2)}`)
  }

  const calculateRiskOfRuin = () => {
    const wr = parseFloat(winRate) / 100
    const avgW = parseFloat(avgWin)
    const avgL = parseFloat(avgLoss)
    const br = parseFloat(bankroll)
    const risk = parseFloat(riskTolerance) / 100

    // Kelly fraction
    const b = avgW / avgL
    const kelly = (b * wr - (1 - wr)) / b

    // Approximate risk of ruin formula
    const edge = wr * avgW - (1 - wr) * avgL
    const variance = wr * avgW * avgW + (1 - wr) * avgL * avgL

    // Risk of ruin approximation (simplified)
    let ror
    if (edge <= 0) {
      ror = 100
    } else {
      const a = (2 * edge) / variance
      ror = Math.exp(-a * br * risk) * 100
    }

    setResult(`Win Rate: ${(wr * 100).toFixed(1)}%\n` +
      `Average Win: $${avgW}\n` +
      `Average Loss: $${avgL}\n` +
      `Bankroll: $${br}\n\n` +
      `Edge per bet: $${edge.toFixed(2)} (${(edge / avgL * 100).toFixed(2)}%)\n` +
      `Kelly Criterion: ${(kelly * 100).toFixed(2)}%\n\n` +
      `Risk of Ruin: ${ror > 100 ? '100' : ror.toFixed(2)}%\n\n` +
      `${ror > 50 ? '⚠️ HIGH RISK - reduce bet size or improve edge' :
         ror > 20 ? '⚠️ MODERATE RISK - consider reducing stakes' :
         ror > 5 ? '✅ ACCEPTABLE RISK' : '✅ LOW RISK'}`)
  }

  const handleCalculate = () => {
    switch (activeCalculator) {
      case 'Kelly Criterion': calculateKellyCriterion(); break
      case 'Arbitrage': calculateArbitrage(); break
      case '+EV Calculator': calculateEV(); break
      case 'Parlay Calculator': calculateParlay(); break
      case 'Hedge Calculator': calculateHedge(); break
      case 'Vig Calculator': calculateVig(); break
      case 'Unit Size Calculator': calculateUnitSize(); break
      case 'Middle Finder': calculateMiddleFinder(); break
      case 'Round Robin Calculator': calculateRoundRobin(); break
      case 'Risk of Ruin Calculator': calculateRiskOfRuin(); break
      default: setResult('')
    }
  }

  const clearInputs = () => {
    setAmerican1(''); setDecimal1(''); setFractional1(''); setImplied1('')
    setAmerican2(''); setDecimal2(''); setFractional2(''); setImplied2('')
    setStake('100'); setProbability('55'); setBankroll('1000'); setUnits('1')
    setParlayLegs(['-110', '+150', '']); setRrLegs(['-110', '+150', '+200', ''])
    setRrSize('2'); setWinRate('55'); setAvgWin('100'); setAvgLoss('100')
    setRiskTolerance('5'); setResult('')
  }

  const getComparisonResult = () => {
    if (!implied1 || !implied2) return null
    const imp1 = parseFloat(implied1)
    const imp2 = parseFloat(implied2)
    const diff = Math.abs(imp1 - imp2).toFixed(2)
    const total = (imp1 + imp2).toFixed(2)
    const higher = imp1 > imp2 ? 'Side 1' : imp2 > imp1 ? 'Side 2' : 'Equal'
    const dec1 = parseFloat(decimal1)
    const dec2 = parseFloat(decimal2)
    const longerOdds = dec1 > dec2 ? 'Side 1' : dec2 > dec1 ? 'Side 2' : 'Equal'

    return { diff, total, higher, longerOdds, isArb: parseFloat(total) < 100 }
  }

  if (!isOpen) return null

  const comparison = getComparisonResult()

  const renderOddsConverter = (side) => {
    const american = side === 1 ? american1 : american2
    const decimal = side === 1 ? decimal1 : decimal2
    const fractional = side === 1 ? fractional1 : fractional2
    const implied = side === 1 ? implied1 : implied2
    const setAmerican = side === 1 ? setAmerican1 : setAmerican2
    const setDecimal = side === 1 ? setDecimal1 : setDecimal2
    const setFractional = side === 1 ? setFractional1 : setFractional2
    const setImplied = side === 1 ? setImplied1 : setImplied2

    return (
      <div className="odds-converter-side">
        <h4>Side {side}</h4>
        <div className="converter-grid">
          <div className="input-group">
            <label>American</label>
            <input
              type="text"
              value={american}
              onChange={(e) => {
                setAmerican(e.target.value)
                updateFromAmerican(e.target.value, side)
              }}
              placeholder="-110"
            />
          </div>
          <div className="input-group">
            <label>Decimal</label>
            <input
              type="text"
              value={decimal}
              onChange={(e) => {
                setDecimal(e.target.value)
                updateFromDecimal(e.target.value, side)
              }}
              placeholder="1.909"
            />
          </div>
          <div className="input-group">
            <label>Fractional</label>
            <input
              type="text"
              value={fractional}
              onChange={(e) => {
                setFractional(e.target.value)
                updateFromFractional(e.target.value, side)
              }}
              placeholder="10/11"
            />
          </div>
          <div className="input-group">
            <label>Implied %</label>
            <input
              type="text"
              value={implied}
              onChange={(e) => {
                setImplied(e.target.value)
                updateFromImplied(e.target.value, side)
              }}
              placeholder="52.38"
            />
          </div>
        </div>
      </div>
    )
  }

  const renderInputs = () => {
    switch (activeCalculator) {
      case 'Odds Converter':
        return (
          <div className="odds-converter-container">
            {renderOddsConverter(1)}
          </div>
        )

      case 'Odds Comparison':
        return (
          <div className="odds-comparison-container">
            <div className="comparison-sides">
              {renderOddsConverter(1)}
              <div className="comparison-middle">
                {comparison && (
                  <>
                    <div className="comparison-stat">
                      <span className="stat-label">Difference</span>
                      <span className="stat-value">{comparison.diff}%</span>
                    </div>
                    <div className="comparison-stat">
                      <span className="stat-label">Total Implied</span>
                      <span className={`stat-value ${comparison.isArb ? 'arb-alert' : ''}`}>
                        {comparison.total}%
                      </span>
                    </div>
                    <div className="comparison-stat">
                      <span className="stat-label">Higher Prob</span>
                      <span className="stat-value highlight">{comparison.higher}</span>
                    </div>
                    <div className="comparison-stat">
                      <span className="stat-label">Longer Odds</span>
                      <span className="stat-value">{comparison.longerOdds}</span>
                    </div>
                    {comparison.isArb && (
                      <div className="arb-alert-box">ARB!</div>
                    )}
                  </>
                )}
              </div>
              {renderOddsConverter(2)}
            </div>
          </div>
        )

      case 'Kelly Criterion':
        return (
          <>
            <div className="input-group">
              <label>Bankroll ($)</label>
              <input type="number" value={bankroll} onChange={(e) => setBankroll(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Odds (American)</label>
              <input type="text" value={american1} onChange={(e) => { setAmerican1(e.target.value); updateFromAmerican(e.target.value, 1) }} placeholder="-110" />
            </div>
            <div className="input-group">
              <label>Win Probability (%)</label>
              <input type="number" value={probability} onChange={(e) => setProbability(e.target.value)} />
            </div>
          </>
        )

      case 'Arbitrage':
        return (
          <>
            <div className="input-group">
              <label>Total Stake ($)</label>
              <input type="number" value={stake} onChange={(e) => setStake(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Odds 1 (American)</label>
              <input type="text" value={american1} onChange={(e) => { setAmerican1(e.target.value); updateFromAmerican(e.target.value, 1) }} placeholder="+150" />
            </div>
            <div className="input-group">
              <label>Odds 2 (American)</label>
              <input type="text" value={american2} onChange={(e) => { setAmerican2(e.target.value); updateFromAmerican(e.target.value, 2) }} placeholder="-130" />
            </div>
          </>
        )

      case '+EV Calculator':
        return (
          <>
            <div className="input-group">
              <label>Stake ($)</label>
              <input type="number" value={stake} onChange={(e) => setStake(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Odds (American)</label>
              <input type="text" value={american1} onChange={(e) => { setAmerican1(e.target.value); updateFromAmerican(e.target.value, 1) }} placeholder="-110" />
            </div>
            <div className="input-group">
              <label>True Win Probability (%)</label>
              <input type="number" value={probability} onChange={(e) => setProbability(e.target.value)} />
            </div>
          </>
        )

      case 'Parlay Calculator':
        return (
          <>
            <div className="input-group">
              <label>Stake ($)</label>
              <input type="number" value={stake} onChange={(e) => setStake(e.target.value)} />
            </div>
            {parlayLegs.map((leg, i) => (
              <div className="input-group" key={i}>
                <label>Leg {i + 1} (American)</label>
                <input
                  type="text"
                  value={leg}
                  onChange={(e) => {
                    const newLegs = [...parlayLegs]
                    newLegs[i] = e.target.value
                    if (i === parlayLegs.length - 1 && e.target.value) newLegs.push('')
                    setParlayLegs(newLegs)
                  }}
                  placeholder="-110"
                />
              </div>
            ))}
          </>
        )

      case 'Hedge Calculator':
        return (
          <>
            <div className="input-group">
              <label>Initial Bet ($)</label>
              <input type="number" value={stake} onChange={(e) => setStake(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Initial Odds (American)</label>
              <input type="text" value={american1} onChange={(e) => { setAmerican1(e.target.value); updateFromAmerican(e.target.value, 1) }} placeholder="+300" />
            </div>
            <div className="input-group">
              <label>Hedge Odds (American)</label>
              <input type="text" value={american2} onChange={(e) => { setAmerican2(e.target.value); updateFromAmerican(e.target.value, 2) }} placeholder="-150" />
            </div>
            <div className="input-group">
              <label>Target Profit ($)</label>
              <input type="number" value={bankroll} onChange={(e) => setBankroll(e.target.value)} />
            </div>
          </>
        )

      case 'Vig Calculator':
        return (
          <>
            <div className="input-group">
              <label>Side 1 Odds (American)</label>
              <input type="text" value={american1} onChange={(e) => { setAmerican1(e.target.value); updateFromAmerican(e.target.value, 1) }} placeholder="-110" />
            </div>
            <div className="input-group">
              <label>Side 2 Odds (American)</label>
              <input type="text" value={american2} onChange={(e) => { setAmerican2(e.target.value); updateFromAmerican(e.target.value, 2) }} placeholder="-110" />
            </div>
          </>
        )

      case 'Unit Size Calculator':
        return (
          <>
            <div className="input-group">
              <label>Bankroll ($)</label>
              <input type="number" value={bankroll} onChange={(e) => setBankroll(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Unit Size (%)</label>
              <input type="number" value={units} onChange={(e) => setUnits(e.target.value)} />
            </div>
          </>
        )

      case 'Middle Finder':
        return (
          <>
            <div className="input-group">
              <label>Spread/Line 1</label>
              <input type="text" value={american1} onChange={(e) => setAmerican1(e.target.value)} placeholder="-3.5" />
            </div>
            <div className="input-group">
              <label>Spread/Line 2</label>
              <input type="text" value={american2} onChange={(e) => setAmerican2(e.target.value)} placeholder="+4.5" />
            </div>
          </>
        )

      case 'Round Robin Calculator':
        return (
          <>
            <div className="input-group">
              <label>Total Stake ($)</label>
              <input type="number" value={stake} onChange={(e) => setStake(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Parlay Size</label>
              <select value={rrSize} onChange={(e) => setRrSize(e.target.value)}>
                <option value="2">2-leg parlays</option>
                <option value="3">3-leg parlays</option>
                <option value="4">4-leg parlays</option>
              </select>
            </div>
            {rrLegs.map((leg, i) => (
              <div className="input-group" key={i}>
                <label>Pick {i + 1}</label>
                <input
                  type="text"
                  value={leg}
                  onChange={(e) => {
                    const newLegs = [...rrLegs]
                    newLegs[i] = e.target.value
                    if (i === rrLegs.length - 1 && e.target.value) newLegs.push('')
                    setRrLegs(newLegs)
                  }}
                  placeholder="-110"
                />
              </div>
            ))}
          </>
        )

      case 'Risk of Ruin Calculator':
        return (
          <>
            <div className="input-group">
              <label>Bankroll ($)</label>
              <input type="number" value={bankroll} onChange={(e) => setBankroll(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Win Rate (%)</label>
              <input type="number" value={winRate} onChange={(e) => setWinRate(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Avg Win ($)</label>
              <input type="number" value={avgWin} onChange={(e) => setAvgWin(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Avg Loss ($)</label>
              <input type="number" value={avgLoss} onChange={(e) => setAvgLoss(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Risk Tolerance (%)</label>
              <input type="number" value={riskTolerance} onChange={(e) => setRiskTolerance(e.target.value)} />
            </div>
          </>
        )

      default:
        return <div className="coming-soon">Select a calculator</div>
    }
  }

  const needsCalculateButton = !['Odds Converter', 'Odds Comparison'].includes(activeCalculator)

  return (
    <div className="calculator-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="calculator-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="calculator-modal-header">
          <h2>Betting Calculators</h2>
          <button className="calculator-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="calculator-tabs">
          {calculators.map(calc => (
            <button
              key={calc}
              className={`calculator-tab ${activeCalculator === calc ? 'active' : ''}`}
              onClick={() => { setActiveCalculator(calc); setResult('') }}
            >
              {calc}
            </button>
          ))}
        </div>
        <div className="calculator-body">
          <div className="calculator-inputs">{renderInputs()}</div>
          {needsCalculateButton && (
            <div className="calculator-buttons">
              <button className="calculate-btn" onClick={handleCalculate}>Calculate</button>
              <button className="clear-btn" onClick={clearInputs}>Clear</button>
            </div>
          )}
          {result && <div className="calculator-result"><h3>Result:</h3><pre>{result}</pre></div>}
        </div>
      </div>
    </div>
  )
}

export default CalculatorModal
